import React, { useMemo } from "react";

import { Button } from "@app/scripts/react-general-components/Button";
import { Drawer } from "@app/scripts/react-general-components/Drawer";
import { useMobile } from "@app/scripts/react-hooks/mobile";
import { useShopify } from "@app/scripts/react-hooks/shopify";
import { ProductDetails } from "@app/scripts/react-product-details";
import { useLocaleContext } from "@app/scripts/react-providers/locales";

import type { NormalisedProduct, Product } from "@app/scripts/types";

type Props = {
  product?: Product | NormalisedProduct;
  btnStyle: "default" | "pin" | "pin-title";
  heading?: "string";
  fgColor?: "white" | "dark-grey";
  className?: string;
};

export const QuickAdd: React.FC<Props> = ({ product, btnStyle, heading, fgColor, className }) => {
  const { product: productLocale } = useLocaleContext();
  const { normaliseProduct } = useShopify();
  const isMobile = useMobile();
  const normalisedProduct = useMemo(
    () =>
      (product as NormalisedProduct)?.is_normalised
        ? (product as NormalisedProduct)
        : normaliseProduct(product as Product),
    [product, normaliseProduct]
  );

  return product ? (
    <Drawer
      heading={productLocale?.quick_add?.quick_add || "Quick Add"}
      drawerClassName="quick-add-drawer"
      opensFrom={isMobile ? "bottom" : "right"}
      customButton={({ toggleOpen }) => (
        <QABtn className={className} toggleOpen={toggleOpen} btnStyle={btnStyle} heading={heading} color={fgColor} />
      )}
    >
      <ProductDetails
        product={normalisedProduct}
        productMediaSliderDesktop
        columnsDesktop={1}
        columnsMobile={1}
        smallButtons
        scrollbarMobile
      />
    </Drawer>
  ) : null;
};

const QABtn: React.FC = ({ btnStyle, toggleOpen, heading, color, className }) => {
  const { product: productLocale } = useLocaleContext();
  const isMobile = useMobile();
  const title = isMobile ? "Add" : productLocale?.quick_add?.quick_add || "Quick Add";

  return btnStyle === "pin" || btnStyle === "pin-title" ? (
    <button
      className={`button button--stl swiper-no-swiping ${btnStyle !== "pin-title" ? "no-text" : ""} ${className || ""}`}
      onClick={toggleOpen}
      aria-label={title}
    >
      <div>
        <ui-icon name="icon-plus" width="16" height="16"></ui-icon>
        {btnStyle === "pin-title" && <span className="hide-mobile">{heading}</span>}
      </div>
    </button>
  ) : (
    <Button
      color={color === "white" ? "outline-white" : color}
      variant="secondary"
      className="fullwidth swiper-no-swiping"
      ariaLabel={title}
      onClick={toggleOpen}
      label={title}
    />
  );
};
